import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import IconV2 from "components/V2/Icons/IconV2";
import InputV2 from "components/V2/Input/InputV2";
import useDebounce from "hooks/useDebounce";
import PagesBar from "components/V2/Pagination/PagesBar";
import Loader from "components/V2/Loader/Loader";
import { IHireCompany } from "types/HireCompany";
import { IHireCompanyFilters } from "types/Filters/HireCompanyFilters";
import HireCompanyApi from "network/hire_company/hire_company";
import Button from "components/Button/ButtonV2";
import { useNavigate } from "react-router-dom";

const PAGE_SIZE = 15;

const HireCompanyOverview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<IHireCompany[]>();
  const [count, setCount] = useState<number | undefined>()
  const [filters, setFilters] = useState<IHireCompanyFilters>({});
  const setFilter = <Key extends keyof IHireCompanyFilters>
    (field: Key, value: IHireCompanyFilters[Key]) => { setFilters({ ...filters, [field]: value }); }

  const debounceSetFilter = useDebounce(setFilter);

  // Fetch one page
  const fetchPage = useMemo(() =>
    async (offset: number): Promise<any[] | undefined> => {
      try {
        const response = await HireCompanyApi.getAll({
          ...filters,
          page_size: PAGE_SIZE,
          offset
        })
        if (!response.data) throw new Error(JSON.stringify(response))
        return response.data
      } catch (e) {
        toast.error('Error while fetching page')
        console.error(e)
      }
    }, [filters]
  );

  // Fetching count is expensive do it separetly
  const fetchCount = async () => {
    try {
      const response = await HireCompanyApi.getAll({
        ...filters,
        count: true,
        page_size: PAGE_SIZE
      })
      if (!response.count) throw new Error(JSON.stringify(response))
      setCount(response.count)
    } catch (e) {
      toast.error('Error while fetching total amount of companies')
      console.error(e)
    }
  };
  useEffect(() => { fetchCount() }, [filters]);

  return (
    <div className="w-full h-full flex flex-col px-[50px]">
      <div className="flex flex-col mt-[52px] w-full gap-5">
        <span className="text-[24px] font-poppins font-bold flex ">
          Debbie Customers and Permissions:&nbsp;
          {count === undefined ? <Loader className='w-[30px] h-[30px]' /> : <span>{count}</span>}
        </span>
        {/* Filters */}
        {/* Search bar */}
        <InputV2
          className="w-[50%]"
          onChange={(e) => debounceSetFilter("keyword", e.target.value)}
          icon={<IconV2 iconType="SEARCH" iconClassName="w-6 h-6 translate-y-[1px]" />}
          placeholder="Search for talent by skills, role, email or name "
        />
      </div>

      <div className="flex flex-col flex-1 w-full overflow-auto mt-[21px] items-center pb-[100px]">
        {/* Data */}
        {/* Table */}
        <table className="table-fixed break-words w-full  border-separate border-spacing-y-6 
            font-bold font-poppins text-[14px]">
          <thead >
            <tr className="[&>th]:bg-[#D9D9D9] [&>th]:py-[12px] [&>th]:h-[62px]">

              <th className="rounded-bl-lg rounded-tl-lg">Company</th>
              <th className="rounded-bl-lg rounded-tl-lg">Plan</th>
              <th>Head Recruiter</th>
              <th>Total Recruiters</th>
              <th>Interviews Completed</th>
              <th>Active Jobs</th>
              <th className="rounded-br-lg rounded-tr-lg">Permissions</th>
            </tr>
          </thead>


          <tbody >
            {(data || []).map(c => {
              const { id, _accounts, accounts_limit, _completed_monthly_job_interviews, completed_monthly_job_interviews_limit, _active_jobs, active_jobs_limit, plan } = c.UsageConstraints;
              return (
                <tr key={id} className="mt-[20px] [&>td]:h-[72px] [&>td]:border-none" >
                  <td className="rounded-bl-lg rounded-tl-lg pl-[20px]">
                    {c.name}
                  </td>
                  <td>{plan}</td>
                  <td>{c.Admin.User.full_name}</td>
                  <td>{_accounts} of {accounts_limit}</td>
                  <td>{_completed_monthly_job_interviews} of {completed_monthly_job_interviews_limit}</td>
                  <td>{_active_jobs} of {active_jobs_limit}</td>
                  <td>
                    <Button onClick={() => navigate("/hire_company/constraints/" + id)} variant="primary"  >View &nbsp;<span className="text-xs">▶</span></Button>
                  </td>
                </tr>)
            })
            }
          </tbody>
        </table>

        {/* Loader */}
        {!data && <div className="w-full h-[100px]"><Loader /></div>}


        {/* Pages bar */}
        <PagesBar
          page_size={PAGE_SIZE}
          className="mt-[8px]"
          count={count}
          fetchPage={fetchPage}
          onLoading={() => setData(undefined)}
          onPageChange={(data) => setData(data)}
        />
      </div>

    </div>);
}
export default HireCompanyOverview;